import { CsvImportFlow } from "components/CompanyCsv/CsvImportFlow";
import { useConfirm } from "components/ConfirmDialog";
import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import {
  useCompaniesApi,
  useCompanyCsv,
  useCompanyMutations,
} from "hooks/useCompaniesApi";
import { Download, Upload } from "lucide-react";
import { type FC, useState } from "react";
import { toastError } from "utils/toast-utils";

import { CompanyCreateForm } from "./CompanyCreateForm";

const PAGE_SIZE = 20;

/**
 * 登録企業一覧画面 (ISSUE-803 Phase 1C)。
 *
 * /settings/companies の登録企業画面。Phase 1 では認証ユーザー全員が CRUD 可能。
 * @return {JSX.Element} 登録企業一覧
 */
export const CompaniesPage: FC = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const { data, loading, error, refetch } = useCompaniesApi({
    page,
    limit: PAGE_SIZE,
    search: search || undefined,
  });
  const { deleteCompany, loading: mutating } = useCompanyMutations();
  const { exportCsv, loading: csvLoading } = useCompanyCsv();
  const { confirm, dialog: confirmDialog } = useConfirm();

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleExport = async () => {
    try {
      await exportCsv({ search: search || undefined });
    } catch (e) {
      toastError(
        `エクスポートに失敗しました: ${e instanceof Error ? e.message : "不明なエラー"}`,
      );
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: "企業を削除",
      message: "この企業を削除します。配信履歴は保持されます。よろしいですか?",
      confirmText: "削除",
      variant: "destructive",
    });
    if (!ok) {
      return;
    }
    try {
      await deleteCompany(id);
      await refetch();
    } catch (e) {
      toastError(
        `削除に失敗しました: ${e instanceof Error ? e.message : "不明なエラー"}`,
      );
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <PageTitle>登録企業</PageTitle>
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={csvLoading}
          >
            <Download className="w-4 h-4" />
            CSV エクスポート
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowImport(true)}
            disabled={csvLoading}
          >
            <Upload className="w-4 h-4" />
            CSV インポート
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => setShowCreate(true)}
            disabled={mutating}
          >
            + 新規作成
          </Button>
        </div>
      </div>

      <CsvImportFlow
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onCompleted={() => {
          refetch().catch(() => undefined);
        }}
      />

      {error && (
        <div className="alert alert-error mb-4">
          <span>エラー: {error.message}</span>
        </div>
      )}

      {/* 検索 */}
      <div className="mb-4 flex gap-2">
        <input
          type="text"
          placeholder="企業名で検索..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSearch()}
          className="input input-bordered input-sm w-full md:w-96"
        />
        <Button type="button" size="sm" onClick={handleSearch}>
          検索
        </Button>
      </div>

      {/* 新規作成フォーム (ISSUE-820/821: 連絡先・企業グループを一括登録) */}
      {showCreate && (
        <CompanyCreateForm
          onCreated={() => {
            setShowCreate(false);
            refetch().catch(() => undefined);
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* 一覧 */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table.Root density="compact" zebra stickyHeader>
          <Table.Head sticky>
            <tr>
              <Table.HeaderCell>企業名</Table.HeaderCell>
              <Table.HeaderCell>主ドメイン</Table.HeaderCell>
              <Table.HeaderCell>連絡先</Table.HeaderCell>
              <Table.HeaderCell>更新日</Table.HeaderCell>
              <Table.HeaderCell>操作</Table.HeaderCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-8">
                  <Loading size="md" />
                </td>
              </tr>
            ) : data?.items.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="text-center py-8 text-base-content/60"
                >
                  企業がまだ登録されていません
                </td>
              </tr>
            ) : (
              data?.items.map(c => (
                <Table.Row key={c.id}>
                  <Table.Cell>
                    <Link to={`/settings/companies/${c.id}`}>{c.name}</Link>
                    {c.description && (
                      <div className="text-base-content/60 text-xs mt-1">
                        {c.description}
                      </div>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm">{c.primaryDomain ?? "—"}</span>
                    {c.domainCount > 1 && (
                      <span className="ml-1 text-xs text-base-content/60">
                        (+{c.domainCount - 1})
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadge tone="soft-neutral">
                      {c.contactCount}
                    </StatusBadge>
                  </Table.Cell>
                  <Table.Cell>
                    {new Date(c.updatedAt).toLocaleDateString("ja-JP")}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      type="button"
                      variant="destructive"
                      size="xs"
                      onClick={() => handleDelete(c.id)}
                      disabled={mutating}
                    >
                      削除
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </div>

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="join">
            <Button
              type="button"
              size="sm"
              isJoinItem
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              «
            </Button>
            <Button type="button" size="sm" isJoinItem>
              {page} / {totalPages}
            </Button>
            <Button
              type="button"
              size="sm"
              isJoinItem
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              »
            </Button>
          </div>
        </div>
      )}
      {confirmDialog}
    </div>
  );
};

export default CompaniesPage;
